import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, BarChartOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '../contexts/AuthContext';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [form] = Form.useForm();
  
  // TV 遥控器焦点管理
  const [focusedElement, setFocusedElement] = useState(0); // 0:用户名, 1:密码, 2:运动管理按钮, 3:运动监测按钮
  const inputRefs = useRef([]);
  const buttonRefs = useRef([]);

  // TV 遥控器键盘导航处理
  useEffect(() => {
    const handleKeyDown = (e) => {
      const totalElements = 4; // 用户名, 密码, 运动管理按钮, 运动监测按钮
      
      switch(e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedElement(prev => {
            const next = prev < totalElements - 1 ? prev + 1 : prev;
            // 聚焦到对应元素
            setTimeout(() => {
              if (next === 0 || next === 1) {
                inputRefs.current[next]?.focus();
              } else {
                buttonRefs.current[next - 2]?.focus();
              }
            }, 0);
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedElement(prev => {
            const next = prev > 0 ? prev - 1 : prev;
            // 聚焦到对应元素
            setTimeout(() => {
              if (next === 0 || next === 1) {
                inputRefs.current[next]?.focus();
              } else {
                buttonRefs.current[next - 2]?.focus();
              }
            }, 0);
            return next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedElement === 2) {
            // 运动管理按钮
            form.submit();
          } else if (focusedElement === 3) {
            // 运动监测按钮
            form.validateFields().then(values => {
              handleMonitorLogin(values);
            }).catch(() => {});
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedElement, form]);
  
  // 初始聚焦到用户名输入框
  useEffect(() => {
    if (!isAuthenticated) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
        setFocusedElement(0);
      }, 100);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // 如果已经登录，直接跳转到仪表盘
    if (isAuthenticated) {
      navigate('/venue/dashboard');
      return;
    }

    const fetchEnumConfig = async () => {
      try {
        const response = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
          tableName: 'LOGIN_ITEM',
          colName: 'LOGIN_ITEM'
        });

        if (response.data.code === 200) {
          const configs = JSON.parse(response.data.data);
          
          const titleConfig = configs.find(config => config.enumCode === 'TITLE');
          const subTitleConfig = configs.find(config => config.enumCode === 'SUB_TITLE');
          
          if (titleConfig) {
            setTitle(titleConfig.enumName);
          }
          
          if (subTitleConfig) {
            setSubTitle(subTitleConfig.enumName);
          }
        }
      } catch (error) {
        console.error('获取枚举配置失败:', error);
      }
    };

    fetchEnumConfig();
  }, [isAuthenticated, navigate]);

  const handleMonitorLogin = async (values) => {
    setLoading(true);
    try {
      // 调用真实登录API
      const loginResponse = await apiClient.post('/portal-server-prod/user/login/v1/login', {
        loginId: values.username,
        password: values.password
      });

      if (loginResponse.data.code === 200) {
        const {
          userName,
          accessToken,
          userId,
          roleCode,
          customerId,
          customerName,
          photoAddress,
          classId,
          coach,
          mqttUserName,
          mqttPassword,
          mqttWebSocketUrl,
        } = loginResponse.data.data;

        const userData = {
          username: values.username,
          userId: userId,
          name: userName,
          role: roleCode,
          customerId: customerId,
          customerName: customerName,
          avatar: photoAddress,
          classId: classId,
          coach: coach,
          mqttUserName: mqttUserName,
          mqttPassword: mqttPassword,
          mqttWebSocketUrl: mqttWebSocketUrl,
        };

        login(userData, accessToken);
        message.success('登录成功！');
        // 跳转到TV端大屏展示页面（运动监测模式）
        navigate('/venue/monitor');
      } else {
        message.error(loginResponse.data.msg || '登录失败！');
      }
    } catch (error) {
      console.error('登录失败:', error);
      if (error.response && error.response.data && error.response.data.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('登录失败，请重试！');
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // 调用真实登录API
      const loginResponse = await apiClient.post('/portal-server-prod/user/login/v1/login', {
        loginId: values.username,
        password: values.password
      });

      if (loginResponse.data.code === 200) {
        const {
          userName,
          accessToken,
          userId,
          roleCode,
          customerId,
          customerName,
          photoAddress,
          classId,
          coach,
          mqttUserName,
          mqttPassword,
          mqttWebSocketUrl,
        } = loginResponse.data.data;
        
        // 检查是否需要强制修改密码（密码过于简单）
        const needChangePassword = loginResponse.data.data.needChangePassword || false;
        
        // 获取菜单列表
        const menuResponse = await apiClient.post('/portal-server-prod/sys/sysrole/v1/roleMenuListByToken', {
          sys: "SportsManagement",
          areaCode: "A00002"
        }, {
          headers: {
            'accessToken': accessToken
          }
        });

        let menuData = [];
        if (menuResponse.data.code === 200) {
          // 解析菜单数据
          const menuList = menuResponse.data.data.list;
          
          // 构建菜单树结构
          const parentMenus = menuList
            .filter(item => !item.parentMenuCode || item.parentMenuCode.trim() === '')
            .sort((a, b) => a.showAsc - b.showAsc);
            
          menuData = parentMenus.map(parent => {
            const children = menuList
              .filter(item => item.parentMenuCode === parent.menuCode)
              .sort((a, b) => a.showAsc - b.showAsc);
              
            return {
              key: parent.menuCode,
              label: parent.name,
              icon: 'TrophyOutlined', // 使用默认图标，后续可以扩展为使用实际图标
              path: parent.url,
              children: children.map(child => ({
                key: child.menuCode,
                label: child.name,
                icon: 'UnorderedListOutlined', // 使用默认图标
                path: child.url
              }))
            };
          });
        }

        const userData = {
          username: values.username,
          userId: userId,
          name: userName,
          role: roleCode,
          customerId: customerId,
          customerName: customerName,
          avatar: photoAddress,
          classId: classId,
          coach: coach,
          mqttUserName: mqttUserName,
          mqttPassword: mqttPassword,
          mqttWebSocketUrl: mqttWebSocketUrl,
          menuData: menuData, // 将菜单数据保存到用户数据中
          needChangePassword: needChangePassword // 添加密码强度标记
        };

        // 新增：登录成功后获取校区（customer）列表并缓存到 localStorage
        try {
          const customerResp = await apiClient.get('/sports-base-server-prod/myPage/searchCustomer?pageNum=1&pageSize=999');
          if (customerResp && customerResp.data && customerResp.data.result === "1" && Array.isArray(customerResp.data.customerList)) {
            // 更新：保存完整的校区信息，包括 phoneDecimal
            const fullCustomerList = customerResp.data.customerList;
            const simplified = fullCustomerList.map(c => ({ 
              customerId: c.customerId, 
              customerName: c.customerName,
              phoneDecimal: c.phoneDecimal // 包含 phoneDecimal 信息
            }));
            try {
              localStorage.setItem('customerList', JSON.stringify(simplified));
            } catch (err) {
              console.error('保存校区列表到 localStorage 失败:', err);
            }
          } else {
            console.warn('获取校区列表返回异常或为空:', customerResp?.data);
          }
        } catch (err) {
          console.error('获取校区列表失败:', err);
        }

        login(userData, accessToken);
        
        if (needChangePassword) {
          message.warning('您的密码过于简单，请修改密码！');
          // 延迟跳转以便用户看到提示
          setTimeout(() => {
            navigate('/venue/dashboard');
          }, 2000);
        } else {
          message.success('登录成功！');
          navigate('/venue/dashboard');
        }
      } else {
        message.error(loginResponse.data.msg || '登录失败！');
      }
    } catch (error) {
      console.error('登录失败:', error);
      // 检查是否有响应错误信息
      if (error.response && error.response.data && error.response.data.msg) {
        message.error(error.response.data.msg);
      } else {
        message.error('登录失败，请重试！');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card 
        className="login-card"
        style={{
          width: 360,
          maxWidth: '100%',
          borderRadius: 12,
          boxShadow: focusedElement >= 0 ? '0 0 0 2px rgba(24, 144, 255, 0.5), 0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease'
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={3} style={{ margin: '0 0 8px 0', color: '#333', fontWeight: 600 }}>
            {title}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>{subTitle}</Text>
        </div>
        
        <Form
          name="login"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          size="large"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名！' }]}
            style={{ marginBottom: 20 }}
          >
            <Input 
              ref={el => inputRefs.current[0] = el}
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
              placeholder="用户名" 
              style={{ 
                height: 42,
                boxShadow: focusedElement === 0 ? '0 0 0 2px rgba(24, 144, 255, 0.4)' : 'none',
                border: focusedElement === 0 ? '1px solid #1890ff' : '1px solid #d9d9d9',
                transition: 'all 0.2s ease'
              }}
              onFocus={() => setFocusedElement(0)}
              onBlur={() => {}}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码！' }]}
            style={{ marginBottom: 28 }}
          >
            <Input.Password 
              ref={el => inputRefs.current[1] = el}
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
              placeholder="密码" 
              style={{ 
                height: 42,
                boxShadow: focusedElement === 1 ? '0 0 0 2px rgba(24, 144, 255, 0.4)' : 'none',
                border: focusedElement === 1 ? '1px solid #1890ff' : '1px solid #d9d9d9',
                transition: 'all 0.2s ease'
              }}
              onFocus={() => setFocusedElement(1)}
              onBlur={() => {}}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button 
              ref={el => buttonRefs.current[0] = el}
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              style={{ 
                height: 52, 
                fontSize: 17, 
                fontWeight: 600,
                borderRadius: 8,
                background: '#1890ff',
                border: '2px solid #1890ff',
                boxShadow: focusedElement === 2 ? '0 0 0 4px rgba(24, 144, 255, 0.5), 0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.15)',
                transform: focusedElement === 2 ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.2s ease'
              }}
              onFocus={() => setFocusedElement(2)}
              onBlur={() => {}}
            >
              <AppstoreOutlined style={{ marginRight: 8 }} /> 运动管理
            </Button>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button 
              ref={el => buttonRefs.current[1] = el}
              onClick={() => {
                form.validateFields().then(values => {
                  handleMonitorLogin(values);
                }).catch(() => {});
              }}
              loading={loading}
              block
              style={{ 
                height: 52, 
                fontSize: 17, 
                fontWeight: 600,
                borderRadius: 8,
                background: '#52c41a',
                border: '2px solid #52c41a',
                color: '#fff',
                boxShadow: focusedElement === 3 ? '0 0 0 4px rgba(82, 196, 26, 0.5), 0 4px 12px rgba(82, 196, 26, 0.3)' : '0 2px 8px rgba(0,0,0,0.15)',
                transform: focusedElement === 3 ? 'scale(1.02)' : 'scale(1)',
                transition: 'all 0.2s ease'
              }}
              onFocus={() => setFocusedElement(3)}
              onBlur={() => {}}
            >
              <BarChartOutlined style={{ marginRight: 8 }} /> 运动监测
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;

